<template lang="pug">
  div
    .mb-6
      h1.display-1.mb-2 Dot Component
      p.text--secondary A small, circular visual indicator used for status, notifications, or decorative purposes. Perfect for showing active states, online presence, or accent marks.

    // Basic Dot Examples
    .component-section.mb-8#basic-dots
      h3.mb-4.cursor-pointer(@click="navigateToSection('basic-dots')") Basic Dot Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in basicVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Dot Implementation
            .d-flex.align-center.gap-4
              .d-flex.align-center.gap-2(v-for="dot in variation.dots" :key="dot.id")
                yz-dot(v-bind="dot.props")
                span(v-if="dot.label") {{ dot.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Size Examples
    .component-section.mb-8#size-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('size-examples')") Size Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in sizeVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Dot Implementation
            .d-flex.align-center.gap-4
              .d-flex.align-center.gap-2(v-for="dot in variation.dots" :key="dot.id")
                yz-dot(v-bind="dot.props")
                span {{ dot.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Color Examples
    .component-section.mb-8#color-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('color-examples')") Color Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in colorVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Dot Implementation
            .d-flex.align-center.gap-4.flex-wrap
              .d-flex.align-center.gap-2(v-for="dot in variation.dots" :key="dot.id")
                yz-dot(v-bind="dot.props")
                span {{ dot.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // Status Examples
    .component-section.mb-8#status-examples
      h3.mb-4.cursor-pointer(@click="navigateToSection('status-examples')") Status Examples
      .variations-grid
        .variation-item.mb-6(v-for="variation in statusVariations" :key="variation.name" :id="variation.id")
          .variation-header.mb-3
            h4.cursor-pointer(@click="navigateToSection(variation.id)") {{ variation.name }}
            p.text--secondary.caption {{ variation.description }}

          .variation-preview.mb-4.pa-4.border.rounded
            .mb-2
              span.font-weight-medium {{ variation.label }}
              small.text--secondary.ml-2(v-if="variation.hint") {{ variation.hint }}
            
            // Dot Implementation
            .d-flex.align-center.gap-4.flex-wrap
              .d-flex.align-center.gap-2(v-for="status in variation.statuses" :key="status.id")
                yz-dot(v-bind="status.props")
                span {{ status.label }}

          .variation-code
            .code-header.d-flex.align-center.mb-2
              yz-btn(icon-only size="md" :icon="copiedCode === variation.code ? 'mdi-check' : 'mdi-content-copy'" :icon-color="copiedCode === variation.code ? 'success' : ''" @click="copyCode(variation.code)")
              span.font-weight-medium.ml-2 Code

            pre.code-block.pa-3.rounded.text-caption(style="background-color: #f5f5f5; overflow-x: auto;")
              code {{ variation.code }}

    // API Documentation
    .component-section.mb-8#api
      h3.mb-4.cursor-pointer(@click="navigateToSection('api')") API
      
      h4.mb-3 Props
      .props-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Prop
                th Type
                th Default
                th Description
            tbody
              tr(v-for="prop in componentProps" :key="prop.name")
                td.font-weight-medium.text-no-wrap {{ prop.name }}
                td.text--secondary.text-no-wrap {{ prop.type }}
                td.text--secondary.text-no-wrap {{ prop.default }}
                td.single-line {{ prop.description }}

      h4.mb-3 Common Use Cases
      .use-cases-table.mb-6
        v-simple-table
          template(v-slot:default)
            thead
              tr
                th Scenario
                th Recommended Approach
                th Example
            tbody
              tr(v-for="useCase in useCases" :key="useCase.scenario")
                td.font-weight-medium.text-no-wrap {{ useCase.scenario }}
                td.text--secondary {{ useCase.approach }}
                td.single-line {{ useCase.example }}

      h4.mb-3 Implementation Notes
      .notes-section
        v-alert(type="info" outlined)
          ul.mb-0
            li
              strong Size: 
              | Use appropriate sizes - 4px-6px for subtle indicators, 8px-12px for prominent status dots
            li
              strong Colors: 
              | Supports design tokens (yz-color-name), hex values, and CSS color names
            li
              strong Positioning: 
              | Often used as absolute positioned elements for notification badges
            li
              strong Accessibility: 
              | Consider adding aria-labels or titles for screen reader context
            li
              strong Performance: 
              | Lightweight component with minimal DOM footprint
</template>

<script>
export default {
  data() {
    return {
      copiedCode: null
    }
  },
  computed: {
    basicVariations() {
      return [
        {
          name: 'Default Dots',
          id: 'default-dots',
          description: 'Basic dots with default styling',
          label: 'Default Dots',
          hint: 'Standard appearance',
          dots: [
            { id: 'default-1', props: {}, label: 'Default' },
            { id: 'default-2', props: { color: 'primary' }, label: 'Primary' },
            { id: 'default-3', props: { color: 'success-500' }, label: 'Success' }
          ],
          code: `yz-dot
yz-dot(color="primary")
yz-dot(color="success-500")`
        }
      ]
    },
    sizeVariations() {
      return [
        {
          name: 'Different Sizes',
          id: 'different-sizes',
          description: 'Dots in various sizes for different use cases',
          label: 'Different Sizes',
          hint: 'Size variations',
          dots: [
            { id: 'small', props: { size: '4px' }, label: '4px' },
            { id: 'medium', props: { size: '8px' }, label: '8px' },
            { id: 'large', props: { size: '12px' }, label: '12px' },
            { id: 'xlarge', props: { size: '16px' }, label: '16px' }
          ],
          code: `yz-dot(size="4px")
yz-dot(size="8px") 
yz-dot(size="12px")
yz-dot(size="16px")`
        }
      ]
    },
    colorVariations() {
      return [
        {
          name: 'Design Token Colors',
          id: 'design-token-colors',
          description: 'Using design system color tokens',
          label: 'Design Token Colors',
          hint: 'yz-color-name format',
          dots: [
            { id: 'success', props: { color: 'success-500', size: '10px' }, label: 'Success' },
            { id: 'warning', props: { color: 'warning-500', size: '10px' }, label: 'Warning' },
            { id: 'danger', props: { color: 'danger-500', size: '10px' }, label: 'Danger' },
            { id: 'info', props: { color: 'blue-500', size: '10px' }, label: 'Info' },
            { id: 'neutral', props: { color: 'neutral-400', size: '10px' }, label: 'Neutral' }
          ],
          code: `yz-dot(color="success-500" size="10px")
yz-dot(color="warning-500" size="10px")
yz-dot(color="danger-500" size="10px")
yz-dot(color="blue-500" size="10px")
yz-dot(color="neutral-400" size="10px")`
        },
        {
          name: 'Custom Hex Colors',
          id: 'custom-hex-colors',
          description: 'Using custom hex color values',
          label: 'Custom Hex Colors',
          hint: 'Direct hex values',
          dots: [
            { id: 'purple', props: { color: '#8B5CF6', size: '10px' }, label: 'Purple' },
            { id: 'pink', props: { color: '#EC4899', size: '10px' }, label: 'Pink' },
            { id: 'teal', props: { color: '#14B8A6', size: '10px' }, label: 'Teal' },
            { id: 'orange', props: { color: '#F97316', size: '10px' }, label: 'Orange' }
          ],
          code: `yz-dot(color="#8B5CF6" size="10px")
yz-dot(color="#EC4899" size="10px")
yz-dot(color="#14B8A6" size="10px")
yz-dot(color="#F97316" size="10px")`
        }
      ]
    },
    statusVariations() {
      return [
        {
          name: 'User Status',
          id: 'user-status',
          description: 'Dots representing user online/offline status',
          label: 'User Status',
          hint: 'Common status indicators',
          statuses: [
            { id: 'online', props: { color: 'success-500', size: '8px' }, label: 'Online' },
            { id: 'away', props: { color: 'warning-500', size: '8px' }, label: 'Away' },
            { id: 'busy', props: { color: 'danger-500', size: '8px' }, label: 'Busy' },
            { id: 'offline', props: { color: 'neutral-400', size: '8px' }, label: 'Offline' }
          ],
          code: `yz-dot(color="success-500" size="8px") // Online
yz-dot(color="warning-500" size="8px") // Away  
yz-dot(color="danger-500" size="8px") // Busy
yz-dot(color="neutral-400" size="8px") // Offline`
        },
        {
          name: 'Notification States',
          id: 'notification-states',
          description: 'Dots for different notification priorities',
          label: 'Notification States',
          hint: 'Priority indicators',
          statuses: [
            { id: 'critical', props: { color: 'danger-500', size: '6px' }, label: 'Critical' },
            { id: 'important', props: { color: 'warning-500', size: '6px' }, label: 'Important' },
            { id: 'normal', props: { color: 'blue-500', size: '6px' }, label: 'Normal' },
            { id: 'low', props: { color: 'neutral-400', size: '6px' }, label: 'Low Priority' }
          ],
          code: `yz-dot(color="danger-500" size="6px") // Critical
yz-dot(color="warning-500" size="6px") // Important
yz-dot(color="blue-500" size="6px") // Normal
yz-dot(color="neutral-400" size="6px") // Low Priority`
        }
      ]
    },
    componentProps() {
      return [
        {
          name: 'color',
          type: 'String',
          default: 'primary',
          description: 'Color of the dot. Supports primary, design tokens (yz-color-name), hex values, or CSS color names'
        },
        {
          name: 'size',
          type: 'String | Number',
          default: '6px',
          description: 'Size of the dot. Accepts px values as string or numbers'
        }
      ]
    },
    useCases() {
      return [
        {
          scenario: 'User status indicators',
          approach: 'Use green for online, yellow for away, red for busy, gray for offline',
          example: 'yz-dot(color="success-500" size="8px")'
        },
        {
          scenario: 'Notification badges',
          approach: 'Small dots (4-6px) positioned absolutely over icons or avatars',
          example: 'yz-dot(color="danger-500" size="6px")'
        },
        {
          scenario: 'List item status',
          approach: 'Use consistent size and color coding for status representation',
          example: 'yz-dot(color="warning-500" size="8px")'
        },
        {
          scenario: 'Progress indicators',
          approach: 'Different colors for completed, current, and pending states',
          example: 'yz-dot(color="success-500" size="10px")'
        },
        {
          scenario: 'Category markers',
          approach: 'Use distinct colors for different categories or tags',
          example: 'yz-dot(color="#8B5CF6" size="8px")'
        }
      ]
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.scrollToSection()
    })
  },
  watch: {
    '$route.hash': {
      handler() {
        this.scrollToSection()
      },
      immediate: false
    }
  },
  methods: {
    navigateToSection(sectionId) {
      this.$router.push({
        name: this.$route.name,
        hash: `#${sectionId}`,
        query: this.$route.query
      })
    },
    scrollToSection() {
      const sectionIdWithHash = this.$route.hash
      const sectionId = sectionIdWithHash.replace('#', '')
      if (sectionId) {
        this.$nextTick(() => {
          const element = document.getElementById(sectionId)
          if (element) {
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            })
          }
        })
      }
    },
    async copyCode(code) {
      try {
        await navigator.clipboard.writeText(code)
        this.copiedCode = code
        setTimeout(() => {
          this.copiedCode = null
        }, 2000)
      } catch (err) {
        console.error('Failed to copy code:', err)
      }
    }
  }
}
</script>

<style scoped>
.variations-grid {
  max-width: 100%;
}

.variation-item {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 20px;
  background-color: #ffffff;
}

.variation-preview {
  background-color: #fafafa;
  border: 1px dashed #d0d0d0 !important;
}

.code-block {
  background-color: #f8f9fa !important;
  border: 1px solid #e9ecef;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  line-height: 1.5;
}

.code-header {
  border-bottom: 1px solid #e0e0e0;
  padding-bottom: 8px;
}

.component-section {
  background-color: #ffffff;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  padding: 24px;
}

.props-table .v-data-table {
  border: 1px solid #e0e0e0;
}

.single-line {
  white-space: normal;
  word-wrap: break-word;
  max-width: 400px;
}

.text-no-wrap {
  white-space: nowrap;
}

.cursor-pointer {
  cursor: pointer;
  transition: color 0.2s ease;
}

.cursor-pointer:hover {
  color: #1976d2;
}

.gap-2 {
  gap: 8px;
}

.gap-4 {
  gap: 16px;
}
</style>